<template>
  <ion-page>
    <ion-content :force-overscroll="false" :scroll-y="currentStep === 2">
      <div class="flex flex-col h-full bg-[#F6F5F1] font-[Inter] safe-area-padding">
        <!-- Header -->
        <div class="px-4 pt-3 pb-1">
          <button
            class="w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-sm"
            @click="handleClose"
          >
            <span class="text-lg text-[#373E49]">&larr;</span>
          </button>
        </div>

        <!-- Step 1: Decorate -->
        <div v-show="currentStep === 1" class="flex-1 flex flex-col min-h-0">
          <EasterEggDecorate
            ref="decorateStep"
            @has-content="hasContent = $event"
            @mode-change="drawMode = $event"
          >
            <template #actions>
              <div v-if="drawMode === 'draw'" class="flex items-center justify-center gap-3">
                <button
                  class="px-5 py-2 rounded-[100px] text-[0.875rem] font-semibold text-[#637083] bg-[#F6F5F1]"
                  @click="$refs.decorateStep?.undo()"
                >
                  Undo
                </button>
                <button
                  class="px-5 py-2 rounded-[100px] text-[0.875rem] font-semibold text-[#637083] bg-[#F6F5F1]"
                  @click="$refs.decorateStep?.clear()"
                >
                  Clear
                </button>
              </div>
              <button
                class="w-full py-[12px] px-[24px] text-[1rem] leading-[1.5rem] font-[700] rounded-[100px] transition-all duration-200 active:scale-[0.98]"
                :class="hasContent ? 'bg-[#FEC629] text-[#21201E]' : 'bg-[#C3C2C1] text-white'"
                :disabled="!hasContent"
                @click="handleSave"
              >
                Save
              </button>
            </template>
          </EasterEggDecorate>
        </div>

        <!-- Step 2: Name Your Egg -->
        <div v-if="currentStep === 2" class="flex-1 flex flex-col items-center justify-center px-6">
          <img
            v-if="exportedImage"
            :src="exportedImage"
            alt="Your Easter egg"
            class="w-[120px] h-[150px] object-contain mb-6"
            :style="{ clipPath: eggClipPath }"
          />
          <h2 class="text-[1.25rem] font-[800] text-[#42362B] mb-2">Name your egg</h2>
          <p class="text-[0.875rem] text-[#6b5d4f] mb-4">Optional — give it a name!</p>
          <input
            v-model="eggNameInput"
            type="text"
            maxlength="50"
            placeholder="e.g. Rainbow Surprise"
            class="w-full max-w-[300px] px-4 py-3 rounded-[12px] border border-[#D3D8DE] bg-white text-[0.9375rem] text-[#21201E] placeholder-[#B0ADA8] focus:outline-none focus:border-[#FEC629] mb-6"
          />
          <div :style="keyboardActive ? `padding-bottom: ${keyboardInsetCss}` : ''">
            <button
              class="w-full max-w-[300px] py-[12px] px-[24px] text-[1rem] leading-[1.5rem] font-[700] bg-[#FEC629] text-[#21201E] rounded-[100px] transition-all duration-200 active:scale-[0.98] mb-3"
              @click="confirmName"
            >
              Save
            </button>
            <button class="text-[0.875rem] font-semibold text-[#637083]" @click="skipName">
              Skip
            </button>
          </div>
        </div>

        <!-- Step 3: Confirmation -->
        <div v-if="currentStep === 3" class="flex-1 flex flex-col">
          <EasterEggConfirmation @done="handleDone" @retry="handleRetry" @share="handleShare" />
        </div>
      </div>
    </ion-content>

    <!-- Leave Confirmation Modal -->
    <Modal v-model="showLeaveModal" :show-close="false">
      <template #header>Leave without saving?</template>
      <p class="text-[1rem] leading-[1.5rem] text-[#2B2926] font-[500]">
        You have unsaved work. Are you sure you want to leave?
      </p>
      <template #footer>
        <button
          class="py-[12px] px-[24px] text-[1rem] leading-[1.5rem] font-[700] text-[#21201E] rounded-[100px] border-solid border-[2px] border-[#42362B]"
          @click="showLeaveModal = false"
        >
          Cancel
        </button>
        <button
          class="py-[12px] px-[24px] text-[1rem] leading-[1.5rem] font-[700] text-[#21201E] bg-[#FEC629] rounded-[100px] hover:bg-[#E6B025] transition-all duration-200 active:scale-[0.98]"
          @click="confirmLeave"
        >
          Leave
        </button>
      </template>
    </Modal>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, useIonRouter } from '@ionic/vue';
import { mapState, mapMutations, mapActions } from 'vuex';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import keyboardOverlap from '@/mixins/keyboard-overlap';
import { isIosNativeOnly, isAndroidNativeOnly } from '@/utils/platform-check';
import { exportEggImage, compressIfNeeded } from '@/utils/egg-export';
import { getEggClipPathCSS } from '@/utils/egg-shape';
import Modal from '@/components/common/Modal.vue';
import EasterEggDecorate from '@/components/EasterEgg/EasterEggDecorate.vue';
import EasterEggConfirmation from '@/components/EasterEgg/EasterEggConfirmation.vue';

export default {
  mixins: [keyboardOverlap],
  name: 'EasterEggPage',
  components: {
    IonPage,
    IonContent,
    Modal,
    EasterEggDecorate,
    EasterEggConfirmation,
  },
  setup() {
    const ionRouter = useIonRouter();
    return { ionRouter };
  },
  data() {
    return {
      hasContent: false,
      drawMode: 'draw',
      showLeaveModal: false,
      leaveTo: null,
      eggNameInput: '',
      eggClipPath: getEggClipPathCSS(),
    };
  },
  computed: {
    ...mapState('easterEgg', ['currentStep', 'exportedImage']),
  },
  beforeRouteLeave(to) {
    if (this.currentStep === 1 && this.hasContent && !this.leaveTo) {
      this.leaveTo = to.fullPath;
      this.showLeaveModal = true;
      return false;
    }
    this.resetEasterEgg();
    this.leaveTo = null;
  },
  ionViewWillEnter() {
    this.resetEasterEgg();
  },
  mounted() {
    this.swipeBlocker = e => {
      if (e.touches[0]?.clientX < 30) {
        e.stopPropagation();
      }
    };
    this.$el?.addEventListener('touchstart', this.swipeBlocker, { capture: true });

    // Keyboard state comes from the keyboard-overlap mixin, which measures how much
    // of the page the keyboard actually covers rather than trusting the reported
    // height — the Android window resizes for the IME on its own.
    this.startKeyboardOverlapTracking();
  },
  beforeUnmount() {
    if (this.swipeBlocker) {
      this.$el?.removeEventListener('touchstart', this.swipeBlocker, { capture: true });
    }
    // No removeAllListeners() here any more: it would tear down listeners this page
    // does not own. The mixin removes its own on unmount.
    this.stopKeyboardOverlapTracking();
  },
  methods: {
    ...mapMutations('easterEgg', ['SET_CURRENT_STEP', 'SET_EXPORTED_IMAGE', 'SET_EGG_NAME']),
    ...mapActions('easterEgg', ['saveEgg', 'resetEasterEgg', 'fetchDesigns']),

    exportDecoratedEgg() {
      const decorateRef = this.$refs.decorateStep;
      if (!decorateRef) return false;

      try {
        const templateCanvas = decorateRef.getTemplateCanvas();
        const drawingCanvas = decorateRef.getDrawingCanvas();
        const photoCanvas = decorateRef.getPhotoCanvas();

        const photoOptions = photoCanvas
          ? {
              canvas: photoCanvas,
              photoData: decorateRef.getPhotoData(),
              photoTransform: decorateRef.getPhotoTransform(),
              displayWidth: decorateRef.getDisplayWidth(),
            }
          : null;

        const base64 = exportEggImage(templateCanvas, drawingCanvas, photoOptions);
        this.SET_EXPORTED_IMAGE(compressIfNeeded(base64));
        return true;
      } catch (error) {
        console.error('Failed to export egg image:', error);
        return false;
      }
    },

    async handleSave() {
      if (!this.exportDecoratedEgg()) return;
      this.eggNameInput = '';
      this.SET_CURRENT_STEP(2); // Go to naming step
    },

    async confirmName() {
      this.SET_EGG_NAME(this.eggNameInput.trim());
      this.SET_CURRENT_STEP(3);
      await this.saveEgg();
    },

    async skipName() {
      this.SET_EGG_NAME('');
      this.SET_CURRENT_STEP(3);
      await this.saveEgg();
    },

    async handleRetry() {
      await this.saveEgg();
    },

    async handleShare() {
      try {
        if (isIosNativeOnly() || isAndroidNativeOnly()) {
          const filePath = await this.saveToTempFile(this.exportedImage);
          await Share.share({
            title: 'My Easter Egg',
            url: filePath,
            dialogTitle: 'Share Easter Egg',
          });
        } else if (navigator.share) {
          const response = await fetch(this.exportedImage);
          const blob = await response.blob();
          const file = new File([blob], 'easter-egg.png', { type: 'image/png' });
          await navigator.share({
            title: 'My Easter Egg',
            files: [file],
          });
        }
      } catch (error) {
        if (error.message !== 'User cancelled' && error.message !== 'Share canceled') {
          console.error('Share failed:', error);
          this.$store.dispatch('toast/error', 'Failed to share');
        }
      }
    },

    async saveToTempFile(base64Image) {
      const base64Data = base64Image.startsWith('data:') ? base64Image.split(',')[1] : base64Image;

      const filePath = `temp/easter-egg-${Date.now()}.png`;

      await Filesystem.writeFile({
        path: filePath,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true,
      });

      const { uri } = await Filesystem.getUri({
        path: filePath,
        directory: Directory.Cache,
      });

      return uri;
    },

    async handleDone() {
      this.hasContent = false;
      this.navigateAway();
    },

    handleClose() {
      if (this.currentStep === 2) {
        this.handleDone();
        return;
      }
      if (this.hasContent) {
        this.leaveTo = null;
        this.showLeaveModal = true;
        return;
      }
      this.navigateAway();
    },

    confirmLeave() {
      const destination = this.leaveTo;
      this.showLeaveModal = false;
      this.$nextTick(() => {
        this.leaveTo = null;
        if (destination) {
          this.resetEasterEgg();
          this.hasContent = false;
          this.$router.push(destination);
        } else {
          this.navigateAway();
        }
      });
    },

    navigateAway() {
      this.resetEasterEgg();
      this.hasContent = false;
      this.ionRouter.back();
    },
  },
};
</script>

<style scoped>
.safe-area-padding {
  padding-top: env(safe-area-inset-top, 0px);
}
</style>
